import { Stack } from '@mantine/core';
import classNames from 'classnames/bind';
import { PageTitle } from 'components/PageTitle';
import { Panel } from 'components/Panel';
import { useCompanyConfiguration } from 'init/use-company-configuration';
import { i18nKeys, useTranslation } from 'locales';
import { useEffect } from 'react';
import type { RouteChildrenProps } from 'react-router';
import { accountActions } from 'store/account/account.actions';

import { INTEGRATIONS_ROUTE, Integration } from '../../Integrations.constants';
import { INTEGRATION_UPDATE_CALLBACK, IntegrationFormFieldsName } from '../Integration.constants';
import styleIdentifiers from '../Integration.module.scss';
import { useGetIntegrationRights } from '../Integration.utils';
import { IntegrationHeader } from '../IntegrationHeader';
import { ExactForm, type ExactFormFields, ExactFormFieldsName } from './ExactForm';

const styles = classNames.bind(styleIdentifiers);

export const Exact = ({ history: { replace } }: RouteChildrenProps) => {
  const { t } = useTranslation();

  const company = useCompanyConfiguration();
  const getIntegrationRights = useGetIntegrationRights();

  const { isActive, canActivate } = getIntegrationRights(Integration.EXACT);

  useEffect(() => {
    if (!canActivate && !isActive) {
      replace(INTEGRATIONS_ROUTE);
    }
  }, [canActivate, isActive, replace]);

  if (!company?.tp_accounting) {
    return (
      <div className={styles('loading')}>
        <div>{t(i18nKeys.LOADING)}</div>
      </div>
    );
  }

  const handleSubmit = (data: ExactFormFields) => {
    accountActions.updateExactSettings({
      data,
      callback: INTEGRATION_UPDATE_CALLBACK,
    });
  };

  const {
    tp_accounting: {
      use_import,
      use_payment_matching,
      additional_parameters,
      issue_date_limit,
      debtor_update,
      automatic_sync,
    },
  } = company;

  return (
    <>
      <PageTitle>
        <PageTitle.Back />
        {Integration.EXACT}
        <PageTitle.Actions>
          <IntegrationHeader name={Integration.EXACT} />
        </PageTitle.Actions>
      </PageTitle>
      <Stack>
        <Panel>
          <ExactForm
            onSubmit={handleSubmit}
            initialValues={{
              [ExactFormFieldsName.IMPORT_INTEGRATION]: use_import,
              [ExactFormFieldsName.REFERENCE_FIELD]: additional_parameters?.reference_field || '',
              [ExactFormFieldsName.PAYMENT_MATCHING]: use_payment_matching,
              [IntegrationFormFieldsName.ISSUE_DATE_LIMIT]: issue_date_limit,
              [IntegrationFormFieldsName.DEBTOR_UPDATE]: debtor_update,
              [IntegrationFormFieldsName.AUTOMATIC_SYNC]: automatic_sync,
            }}
          />
        </Panel>
      </Stack>
    </>
  );
};
